/**
 * @file ConsentPreviewControls.tsx
 * @description Переключатели предпросмотра баннера (phase19.16, Р3г;
 * uiux-r3g-consent-texts §3): Вид, Форма, Сервисы + Вебвизор, Ширина. На
 * телефоне «Форма» — кнопка со шторкой выбора (нативный select запрещён),
 * «Ширина» на планшете — «360 | 728», с 1024 — «360 | ПК».
 */

import { ChevronDown } from "lucide-react";
import { useState } from "react";

import { LG_QUERY, PHONE_QUERY, useMediaQuery } from "../../../hooks/useMediaQuery";
import { PREVIEW_SERVICES_OPTIONS, type PreviewServicesPreset } from "../../../lib/platform/consent-preview";
import { CheckRow } from "../../site-view/CheckRow";
import { Segmented } from "../../site-view/Segmented";
import { Button } from "../../ui/button";
import { GroupField } from "../../ui/settings-groups";
import { OptionSheet } from "../../ui/OptionSheet";
import { PREVIEW_FORM_OPTIONS, PREVIEW_VIEW_OPTIONS, type PreviewForm, type PreviewState, type PreviewWidth } from "./preview-options";

interface Props {
  state: PreviewState;
  onChange: (patch: Partial<PreviewState>) => void;
}

export function ConsentPreviewControls({ state, onChange }: Props) {
  const phone = useMediaQuery(PHONE_QUERY);
  const desktop = useMediaQuery(LG_QUERY);
  const [formSheet, setFormSheet] = useState(false);
  const formLabel = PREVIEW_FORM_OPTIONS.find((o) => o.value === state.form)?.label ?? "";
  const widthOptions: { value: PreviewWidth; label: string }[] = [
    { value: "narrow", label: "360" },
    { value: "wide", label: desktop ? "ПК" : "728" },
  ];
  const noServices = state.view === "info";

  return (
    <div className="flex flex-col gap-3">
      <GroupField label="Вид">
        <Segmented aria-label="Вид баннера" options={PREVIEW_VIEW_OPTIONS} value={state.view} onChange={(view) => onChange({ view })} />
      </GroupField>
      <GroupField label="Форма">
        {phone ? (
          <>
            <Button variant="outline" className="min-h-12 justify-between self-start" onClick={() => setFormSheet(true)}>
              {formLabel}
              <ChevronDown className="size-4" aria-hidden />
            </Button>
            <OptionSheet
              open={formSheet}
              onOpenChange={setFormSheet}
              title="Форма баннера"
              value={state.form}
              options={PREVIEW_FORM_OPTIONS.map((o) => ({ value: o.value, label: o.label }))}
              onChange={(value) => onChange({ form: value as PreviewForm })}
            />
          </>
        ) : (
          <Segmented aria-label="Форма баннера" options={PREVIEW_FORM_OPTIONS} value={state.form} onChange={(form) => onChange({ form })} />
        )}
      </GroupField>
      <div className="flex flex-wrap items-end gap-x-4 gap-y-2">
        <GroupField label="Сервисы">
          <Segmented
            aria-label="Сервисы образца"
            options={PREVIEW_SERVICES_OPTIONS.map((o) => ({ ...o, disabled: noServices }))}
            value={state.services}
            onChange={(services: PreviewServicesPreset) => onChange({ services })}
          />
        </GroupField>
        <CheckRow checked={state.webvisor} onChange={(webvisor) => onChange({ webvisor })} disabled={noServices || state.services === "vk"}>
          Вебвизор
        </CheckRow>
      </div>
      <GroupField label="Ширина">
        <Segmented aria-label="Ширина образца" options={widthOptions} value={state.width} onChange={(width) => onChange({ width })} />
      </GroupField>
    </div>
  );
}
